<script lang="ts" setup>
import { v4 as uuid } from 'uuid'
import { useRouter } from 'vue-router/composables'
import type { RankingPageConfig } from '../configs/types'
import { useRanking } from '../composables/useRanking'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'

interface Props {
  columnId: string
  config: RankingPageConfig
}

const props = defineProps<Props>()

const router = useRouter()

const {
  title,
  books,
  collections,
  fetching,
  ending,
  infiniteRef,
} = useRanking(props.columnId, props.config)

function gotoBook(id: string, firstChapterId?: string) {
  router.push({
    name: READER_ROUTE_NAME,
    params: { contentId: id, chapterId: firstChapterId || '' },
  })
}

// 排行序号档位(设计稿:1 金 / 2 银 / 3 铜,4 名起 ink3)
function getMedalTier(index: number) {
  if (index === 0)
    return 'gold'
  if (index === 1)
    return 'silver'
  if (index === 2)
    return 'bronze'
  return 'normal'
}
</script>

<template>
  <div class="page novelyn-ranking-page h-100dvh overflow-scroll">
    <div class="novelyn-ranking-header">
      <!-- 标题放进 BasicNavbar 插槽内,避免被 fixed 导航栏(半透明背景+blur)盖住 -->
      <BasicNavbar class="color-n-text-2">
        <div class="title">
          {{ title || '' }}
        </div>
      </BasicNavbar>
    </div>

    <div ref="infiniteRef" class="container bg-n-bg-card rounded" :class="{ 'is-empty': !collections.length }">
      <div v-for="(book, index) in collections" :key="index">
        <div
          class="flex items-center px-3 py-2"
          @click="gotoBook(book.id, book.firstChapter?.id)"
        >
          <!-- 排行序号:设计稿为金/银/铜彩色斜体 Playfair 数字,4 名起 ink3 小号 -->
          <div
            class="medal"
            :class="[`medal--${getMedalTier(index)}`, { 'medal--two-digit': index + 1 >= 10 }]"
          >
            {{ index + 1 }}
          </div>

          <div class="w-66px h-88px border-rd-8px overflow-hidden ml-12px shrink-0">
            <BizBookCover
              :id="uuid()"
              :src="book.contentCoverUrl || ''"
              :extra="{
                module_name: books.showName,
                module_id: books.id,
                module_type: books.columnType,
                author: book.authorInfo.author,
                ...book,
              }"
              class="w-full h-full"
            />
          </div>

          <div class="flex-1 overflow-hidden ml-3">
            <div class="font-500 truncate">
              {{ book.contentName }}
            </div>

            <div class="text-n-text-3 text-12px line-clamp-2 mt-2">
              {{ book.introduce }}
            </div>
            <BizShowTags v-if="book.tagList && book.tagList.length" :tags="book.tagList" class="mt-10px" />
          </div>
        </div>
      </div>
    </div>

    <!-- loading/结束提示在卡片外,与栏目页一致 -->
    <div v-if="ending" class="py-15px text-13px text-n-text-3 text-center" :class="{ 'mt-50px': !collections.length }">
      {{ config.texts.noMore }}
    </div>
    <div v-else-if="fetching" class="py-15px flex items-center justify-center space-x-4 text-13px text-n-text-3" :class="{ 'mt-50px': !collections.length }">
      <div class="animate-spin">
        <i-icon-park-outline-loading />
      </div>
      <p>{{ config.texts.loading }}</p>
    </div>
  </div>
</template>

<style scoped>
.page {
  padding: 0 16px 24px;
  background: var(--novelyn-bg);
}

.novelyn-ranking-header {
  position: sticky;
  top: 0;
  z-index: 5;
  height: 48px;
  margin: 0 -16px;
  background: rgba(253, 246, 247, .96);
  backdrop-filter: blur(12px);
}

.title {
  position: absolute;
  inset: 0 58px;
  display: grid;
  place-items: center;
  overflow: hidden;
  pointer-events: none;
  color: var(--novelyn-ink);
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 17px;
  font-weight: 700;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* 排行序号(设计稿 .medal:34px 序号列,斜体 Playfair 800) */
.medal {
  width: 34px;
  flex-shrink: 0;
  text-align: center;
  font-family: 'Playfair Display', Georgia, serif;
  font-style: italic;
  font-weight: 800;
  font-size: 22px;
  line-height: 1;
  color: var(--novelyn-gold);
  align-self: center;

  &.medal--silver {
    color: #B9A5AF;
  }

  &.medal--bronze {
    color: #D8A08A;
  }

  &.medal--normal {
    font-size: 17px;
    color: var(--novelyn-ink-3);
  }

  /* 两位序号(10 名起)缩小,避免溢出 34px 序号列 */
  &.medal--two-digit {
    font-size: 14px;
  }
}

.container {
  height: auto;
  margin-top: 12px;
  padding: 2px 14px;
  overflow: visible;
  border-radius: 20px;
  background: #fff;
  box-shadow: var(--novelyn-shadow-sm);

  /* 空卡片(加载中):去掉白底,避免出现白色细条(loading 已在卡片外) */
  &.is-empty {
    padding: 0;
    background: transparent;
    box-shadow: none;
  }

  > div + div {
    border-top: 1px solid var(--novelyn-line);
  }
}
</style>
